import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAuthHeaders } from '../services/api';

interface ParsedLogEntry {
	ts: string;
	level: string;
	msg: string;
	src: string;
	raw: string;
}

interface AgentLogViewerProps {
	title: string;
	fetchUrl: string;
	onClose?: () => void;
}

function parseLogLine(line: string): ParsedLogEntry {
	try {
		const parsed = JSON.parse(line);
		return {
			ts: parsed.ts || '',
			level: parsed.level || 'info',
			msg: parsed.msg || line,
			src: parsed.src || 'agent',
			raw: line,
		};
	} catch {
		return { ts: '', level: 'info', msg: line, src: 'agent', raw: line };
	}
}

function formatTimestamp(ts: string): string {
	if (!ts) return '';
	try {
		const d = new Date(ts);
		return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
	} catch {
		return ts.slice(11, 19);
	}
}

const LEVEL_COLORS: Record<string, string> = {
	error: 'text-red-400',
	warning: 'text-yellow-400',
	info: 'text-green-300',
};

const SOURCE_COLORS: Record<string, string> = {
	Slack: 'text-purple-400',
	Discord: 'text-indigo-400',
	Telegram: 'text-sky-400',
	AdapterManager: 'text-cyan-400',
	AdapterSync: 'text-cyan-400',
};

export default function AgentLogViewer({ title, fetchUrl, onClose }: AgentLogViewerProps) {
	const { firebaseUser } = useAuth();
	const [lines, setLines] = useState<string[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [sourceFilter, setSourceFilter] = useState<string>('all');
	const logsEndRef = useRef<HTMLDivElement>(null);

	const fetchLogs = useCallback(async () => {
		if (!firebaseUser) return;
		setLoading(true);
		setError(null);
		try {
			const idToken = await firebaseUser.getIdToken();
			const response = await fetch(fetchUrl, {
				headers: getAuthHeaders(idToken),
			});
			const data = await response.json();
			if (data.error) {
				setError(data.error);
				setLines([]);
			} else {
				setLines(data.lines || []);
			}
		} catch (err: any) {
			setError(err.message || 'Failed to fetch logs');
		} finally {
			setLoading(false);
		}
	}, [fetchUrl, firebaseUser]);

	useEffect(() => {
		fetchLogs();
	}, [fetchLogs]);

	const parsedLines = useMemo(() => lines.map(parseLogLine), [lines]);

	// Extract unique sources for filter dropdown
	const sources = useMemo(() => {
		const set = new Set(parsedLines.map(l => l.src));
		return Array.from(set).sort();
	}, [parsedLines]);

	const filteredLines = useMemo(() => {
		if (sourceFilter === 'all') return parsedLines;
		if (sourceFilter === 'adapters') {
			const adapterSources = ['Slack', 'Discord', 'Telegram', 'AdapterManager', 'AdapterSync'];
			return parsedLines.filter(l => adapterSources.includes(l.src));
		}
		return parsedLines.filter(l => l.src === sourceFilter);
	}, [parsedLines, sourceFilter]);

	useEffect(() => {
		logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [filteredLines]);

	const copyToClipboard = useCallback(() => {
		const text = filteredLines.map(l => l.raw).join('\n');
		navigator.clipboard.writeText(text);
	}, [filteredLines]);

	return (
		<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
			{/* Header */}
			<div className="px-4 py-3 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
				<div className="flex items-center space-x-3">
					<div className={`w-2 h-2 rounded-full ${loading ? 'bg-yellow-500 animate-pulse' : error ? 'bg-red-500' : 'bg-green-500'}`} />
					<span className="text-sm font-medium text-slate-700 dark:text-slate-300">
						{title}
					</span>
				</div>
				<div className="flex items-center space-x-2">
					{/* Source filter */}
					<select
						value={sourceFilter}
						onChange={e => setSourceFilter(e.target.value)}
						className="text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 outline-none"
					>
						<option value="all">All Sources</option>
						<option value="adapters">Adapters Only</option>
						{sources.map(src => (
							<option key={src} value={src}>{src}</option>
						))}
					</select>
					<button
						onClick={fetchLogs}
						disabled={loading}
						className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors disabled:opacity-50"
						title="Refresh"
					>
						<svg className={`w-4 h-4 text-slate-500 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
						</svg>
					</button>
					<button
						onClick={copyToClipboard}
						disabled={filteredLines.length === 0}
						className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors disabled:opacity-50"
						title="Copy to clipboard"
					>
						<svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
						</svg>
					</button>
					{onClose && (
						<button
							onClick={onClose}
							className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
						>
							<svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					)}
				</div>
			</div>

			{/* Log Content */}
			<div className="bg-slate-950 text-green-400 font-mono text-xs h-80 overflow-y-auto p-4">
				{loading ? (
					<div className="text-slate-500 text-center py-8">
						Fetching logs...
					</div>
				) : error ? (
					<div className="text-red-400 text-center py-8">
						{error}
					</div>
				) : filteredLines.length === 0 ? (
					<div className="text-slate-500 text-center py-8">
						{sourceFilter !== 'all' ? `No log lines matching filter "${sourceFilter}".` : 'No log lines found.'}
					</div>
				) : (
					<div className="space-y-0.5">
						{filteredLines.map((entry, i) => (
							<div key={i} className="flex items-start gap-1 leading-snug">
								{entry.ts && (
									<span className="text-slate-500 shrink-0 whitespace-nowrap">{formatTimestamp(entry.ts)}</span>
								)}
								<span className={`shrink-0 ${LEVEL_COLORS[entry.level] || 'text-green-300'}`}>
									{entry.level === 'error' ? 'ERR' : entry.level === 'warning' ? 'WRN' : 'INF'}
								</span>
								<span className={`shrink-0 whitespace-nowrap ${SOURCE_COLORS[entry.src] || 'text-slate-400'}`}>
									[{entry.src}]
								</span>
								<span className={`break-all ${LEVEL_COLORS[entry.level] || 'text-green-300'}`}>
									{entry.msg}
								</span>
							</div>
						))}
						<div ref={logsEndRef} />
					</div>
				)}
			</div>

			{/* Footer */}
			<div className="px-4 py-2 bg-slate-100 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
				<span className="text-xs text-slate-500 dark:text-slate-400">
					{filteredLines.length}{sourceFilter !== 'all' ? ` / ${lines.length}` : ''} line{filteredLines.length !== 1 ? 's' : ''}
				</span>
			</div>
		</div>
	);
}
